"use client";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteEvent } from "@/hooks/useDeleteModal";
import { useNewsNoticesModal } from "@/hooks/useNewsNoticesModal";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import type { Row } from "@tanstack/react-table";

type NewsNotice = {
  id: string;
  noticeType: string;
  title: string;
  description: string;
  author: string | null;
  category: string | null;
  tags: string[];
  priority: string;
  status: string;
  date: Date;
  expirationDate: Date | null;
  featuredImageId: string | null;
  attachmentId: string | null;
};

interface DataTableRowActionsProps<TData> {
  row: Row<TData>;
}

export function DataTableRowActions<TData>({
  row,
}: DataTableRowActionsProps<TData>) {
  const newsNotice = row.original as NewsNotice;
  const setDeleteId = useDeleteEvent((s) => s.setToDeleteId);
  const setNewsNoticeId = useNewsNoticesModal((s) => s.setNewsNoticeId);
  const setIsModalOpen = useNewsNoticesModal((s) => s.setIsModalOpen);
  const setDefaultValues = useNewsNoticesModal((s) => s.setDefaultValues);

  const handleArchive = () => {
    setNewsNoticeId(newsNotice.id);
    setDefaultValues({
      noticeType: newsNotice.noticeType as "news" | "notice",
      title: newsNotice.title,
      description: newsNotice.description,
      author: newsNotice.author || "",
      category: newsNotice.category || "",
      tags: newsNotice.tags,
      priority: newsNotice.priority as "low" | "normal" | "high" | "urgent",
      status: newsNotice.status === "archived" ? "published" : "archived",
      date: new Date(newsNotice.date),
      expirationDate: newsNotice.expirationDate
        ? new Date(newsNotice.expirationDate)
        : undefined,
      featuredImageId: newsNotice.featuredImageId || undefined,
      attachmentId: newsNotice.attachmentId || undefined,
    });
    setIsModalOpen(true);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
        >
          <DotsHorizontalIcon className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[160px]">
        <DropdownMenuItem
          onClick={() => {
            setNewsNoticeId(newsNotice.id);
            setDefaultValues({
              noticeType: newsNotice.noticeType as "news" | "notice",
              title: newsNotice.title,
              description: newsNotice.description,
              author: newsNotice.author || "",
              category: newsNotice.category || "",
              tags: newsNotice.tags,
              priority: newsNotice.priority as
                | "low"
                | "normal"
                | "high"
                | "urgent",
              status: newsNotice.status as "draft" | "published" | "archived",
              date: new Date(newsNotice.date),
              expirationDate: newsNotice.expirationDate
                ? new Date(newsNotice.expirationDate)
                : undefined,
              featuredImageId: newsNotice.featuredImageId || undefined,
              attachmentId: newsNotice.attachmentId || undefined,
            });
            setIsModalOpen(true);
          }}
        >
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setNewsNoticeId(newsNotice.id);
            setDefaultValues({
              noticeType: newsNotice.noticeType as "news" | "notice",
              title: `${newsNotice.title} (Copy)`,
              description: newsNotice.description,
              author: newsNotice.author || "",
              category: newsNotice.category || "",
              tags: newsNotice.tags,
              priority: newsNotice.priority as
                | "low"
                | "normal"
                | "high"
                | "urgent",
              status: "draft",
              date: new Date(),
              expirationDate: newsNotice.expirationDate
                ? new Date(newsNotice.expirationDate)
                : undefined,
              featuredImageId: newsNotice.featuredImageId || undefined,
              attachmentId: newsNotice.attachmentId || undefined,
            });
            setNewsNoticeId(null); // Clear ID to create duplicate
            setIsModalOpen(true);
          }}
        >
          Duplicate
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleArchive}>
          {newsNotice.status === "archived" ? "Unarchive" : "Archive"}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            setDeleteId(newsNotice.id);
          }}
        >
          Delete
          <DropdownMenuShortcut>⌘⌫</DropdownMenuShortcut>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
